import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { CreditAllowanceNudgeThreshold } from '../credit-limit-reached-upgrade/types';

export type CreditAllowanceThresholdConsumedState = Record<string, true>;

/**
 * Each surface remembers its own dismissal: closing the side-nav popover
 * (including on outside click) must not hide the in-chat banner, which stays
 * until the user dismisses it.
 */
export type CreditAllowanceThresholdSurface = 'popover' | 'banner';

export function buildConsumedKey(
  billingPlanKey: string,
  cycleEnd: string,
  threshold: CreditAllowanceNudgeThreshold,
  surface: CreditAllowanceThresholdSurface = 'popover'
): string {
  // 'popover' keeps the unsuffixed v1 key so already-dismissed popovers stay dismissed.
  const suffix = surface === 'popover' ? '' : `:${surface}`;
  return `${billingPlanKey}:${cycleEnd}:${threshold}${suffix}`;
}

export function isThresholdConsumedInRecord(
  consumed: CreditAllowanceThresholdConsumedState,
  billingPlanKey: string,
  cycleEnd: string,
  threshold: CreditAllowanceNudgeThreshold,
  surface: CreditAllowanceThresholdSurface = 'popover'
): boolean {
  return (
    consumed[buildConsumedKey(billingPlanKey, cycleEnd, threshold, surface)] === true
  );
}

const createSsrSafeLocalStorage = () =>
  createJSONStorage<CreditAllowanceThresholdConsumedState>(() => {
    if (typeof window === 'undefined') {
      return {
        getItem: () => null,
        setItem: () => undefined,
        removeItem: () => undefined,
      };
    }
    return localStorage;
  });

/** Reactive consumed-threshold ledger — launch queue + popover read this atom. */
export const creditAllowanceThresholdConsumedAtom =
  atomWithStorage<CreditAllowanceThresholdConsumedState>(
    'heidi.creditAllowanceThresholdPopover:v1:consumed',
    {},
    createSsrSafeLocalStorage(),
    { getOnInit: true }
  );

export const markCreditAllowanceThresholdConsumedAtom = atom(
  null,
  (
    get,
    set,
    {
      billingPlanKey,
      cycleEnd,
      threshold,
      surface = 'popover',
    }: {
      billingPlanKey: string;
      cycleEnd: string;
      threshold: CreditAllowanceNudgeThreshold;
      surface?: CreditAllowanceThresholdSurface;
    }
  ) => {
    const consumedKey = buildConsumedKey(billingPlanKey, cycleEnd, threshold, surface);
    const consumed = get(creditAllowanceThresholdConsumedAtom);
    if (consumed[consumedKey]) {
      return;
    }

    set(creditAllowanceThresholdConsumedAtom, {
      ...consumed,
      [consumedKey]: true,
    });
  }
);
